import PrivatePdfUpload from "./PrivatePdfUpload";
import React, { Component } from "react";
import { Form, FormikProps, withFormik } from "formik";
import * as Yup from "yup";
import Button from "@material-ui/core/Button";

import ActionFormButton from "../../../common/ActionFormButton";
import ImageUpload from "../../../common/ImageUpload";
import {
  getRowItemObject,
  renderRowWithSingleItem,
  renderRowWithTwoItems,
  renderSectionTitle,
  Section,
  Wrapper,
} from "../../../common/FormUtils";
import { Shop, Product } from "../../../../types/Shop";
import { useAllProducts } from "../../../../database/shop/products/getAllProducts";
import { createProduct } from "../../../../database/shop/products/createProduct";
import { updateProduct } from "../../../../database/shop/products/updateProduct";
import { deleteProduct } from "../../../../database/shop/products/deleteProduct";

type FormValues = {
  id: string;
  shopName: string;
  currency: string;
  isActive: string;
  paymentLink: string;
  paypalEmail: string;
};

type OuterProps = {
  onChageFormToEditMode: () => void;
  onRemoveItem: () => void;
  onCreateItem: (shop: Shop) => void | Promise<void>;
  onEditItem: (shop: Shop) => void | Promise<void>;
  mode: string;
  item: Shop;
  communityId?: string;
  loadedProducts?: Product[];
  isActive?: boolean;
};

type Props = FormikProps<FormValues> & OuterProps;

type State = {
  products: Product[];
  editingProducts: { [key: string]: Product };
  productError: string;
};

class ShopForm extends Component<Props, State> {
  state: State = {
    products: [],
    editingProducts: {},
    productError: "",
  };

  componentDidMount() {
    this.loadProducts();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.communityId !== this.props.communityId ||
      prevProps.loadedProducts !== this.props.loadedProducts
    ) {
      this.loadProducts();
    }
  }

  loadProducts = () => {
    const { loadedProducts } = this.props;
    if (loadedProducts) {
      this.setState({ products: loadedProducts });
    }
  };

  addProduct = async () => {
    const { communityId } = this.props;
    if (!communityId) return;

    const newProduct: Product = {
      id: `product-${Date.now()}`,
      name: "",
      description: "",
      price: 0,
      currency: this.props.item?.currency || "USD",
      imageURL: "",
      category: "",
      index: this.state.products.length,
      isVisible: false,
      itemLink: "",
    };

    this.setState((prevState) => ({
      products: prevState.products,
      editingProducts: {
        ...prevState.editingProducts,
        [newProduct.id]: newProduct,
      },
    }));
  };

  saveProduct = async (product: Product) => {
    const { communityId } = this.props;
    if (!communityId) return;

    this.setState({ productError: "" });
    try {
      if (this.state.products.find((p) => p.id === product.id)) {
        await updateProduct({ communityId, product });
      } else {
        await createProduct({ communityId, product });
      }

      this.setState((prevState) => {
        const { [product.id]: _, ...remaining } = prevState.editingProducts;
        return { editingProducts: remaining };
      });
    } catch (error) {
      const serverMessage = typeof (error as any)?.message === "string" && (error as any).message.trim() && !/^internal$/i.test((error as any).message.trim()) ? (error as any).message.trim() : "";
      this.setState({ productError: serverMessage || "Could not save product. Check the name, price and community access. Never paste payment keys into product fields." });
    }
  };

  removeProduct = async (productId: string) => {
    const { communityId } = this.props;
    if (!communityId) return;

    try {
      await deleteProduct({ communityId, productId });

      this.setState((prevState) => {
        const { [productId]: _, ...remaining } = prevState.editingProducts;
        return { editingProducts: remaining };
      });
    } catch (error) {
      this.setState({ productError: "Could not remove product. Check your community access and retry." });
    }
  };

  updateEditingProduct = (productId: string, field: keyof Product, value: any) => {
    this.setState((prevState) => {
      const product = prevState.editingProducts[productId];
      if (!product) return null;

      return {
        products: prevState.products,
        editingProducts: {
          ...prevState.editingProducts,
          [productId]: {
            ...product,
            [field]: value,
          },
        },
      };
    });
  };

  startEditingProduct = (product: Product) => {
    this.setState((prevState) => ({
      products: prevState.products,
      editingProducts: {
        ...prevState.editingProducts,
        [product.id]: { ...product },
      },
    }));
  };

  cancelEditingProduct = (productId: string) => {
    this.setState((prevState) => {
      const { [productId]: _, ...remaining } = prevState.editingProducts;
      return {
        products: prevState.products,
        editingProducts: remaining
      };
    });
  };

  createProductFieldForImageUpload = (productId: string) => {
    const product = this.state.editingProducts[productId];
    return {
      name: `product-${productId}-imageURL`,
      value: product?.imageURL || "",
    };
  };

  createProductFormForImageUpload = (productId: string) => {
    return {
      setFieldValue: (_fieldName: string, value: string) => {
        this.updateEditingProduct(productId, "imageURL", value);
      },
    };
  };

  renderBasicInfoSection = () => {
    const shopNameInput = getRowItemObject(
      "Shop name",
      "Enter the shop name",
      "text",
      "shopName"
    );
    const currencyInput = getRowItemObject(
      "Currency",
      "e.g. USD, EUR, MXN",
      "text",
      "currency"
    );
    const isActiveInput = getRowItemObject(
      "Is active",
      "true / false",
      "text",
      "isActive"
    );

    return (
      <Section>
        {renderSectionTitle("Basic settings")}
        {renderRowWithTwoItems(shopNameInput, currencyInput, this.props)}
        {renderRowWithSingleItem(isActiveInput, this.props)}
      </Section>
    );
  };

  renderPaymentSection = () => {
    const paymentLinkInput = getRowItemObject(
      "Payment link",
      "Payment gateway URL",
      "text",
      "paymentLink"
    );
    const paypalEmailInput = getRowItemObject(
      "PayPal email",
      "PayPal account email",
      "text",
      "paypalEmail"
    );

    return (
      <Section>
        {renderSectionTitle("Payment")}
        {renderRowWithSingleItem(paymentLinkInput, this.props)}
        {renderRowWithSingleItem(paypalEmailInput, this.props)}
      </Section>
    );
  };

  renderProductsSection = () => {
    const { mode } = this.props;
    const { products, editingProducts } = this.state;

    // Get all products that are not being edited
    const displayProducts = products.filter(
      (p) => !editingProducts[p.id]
    );

    return (
      <Section>
        {renderSectionTitle("Products")}
        {this.state.productError && <p role="alert">{this.state.productError}</p>}
        <p style={{ color: "#888", fontSize: 12, marginBottom: 16 }}>
          Products are saved individually. Click "Edit" to modify or "Save" after making changes.
        </p>
        <div style={{ marginBottom: 16 }}>
          {/* Show products that are not being edited */}
          {displayProducts.map((product) => (
            <div
              key={product.id}
              style={{
                marginBottom: 16,
                padding: 16,
                border: "1px solid #ddd",
                borderRadius: 4,
                backgroundColor: "#fff",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 style={{ margin: 0 }}>{product.name || "Unnamed Product"}</h4>
                {mode !== "detail" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => this.startEditingProduct(product)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => this.removeProduct(product.id)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </div>
              <p style={{ margin: "8px 0 0 0", fontSize: 12, color: "#666" }}>
                Price: {product.currency || this.props.item?.currency || "USD"} {product.price}{product.billingInterval ? ` / ${product.billingInterval}` : ""} | Category: {product.category || "N/A"} | Index: {product.index}
              </p>
            </div>
          ))}

          {/* Show products being edited */}
          {Object.values(editingProducts).map((product) => (
            <div
              key={product.id}
              style={{
                marginBottom: 16,
                padding: 16,
                border: "1px solid #ddd",
                borderRadius: 4,
                backgroundColor: "#f9f9f9",
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: 12 }}>
                {product.name || "New Product"}
              </h4>
              <fieldset style={{ marginBottom: 16, padding: 12 }} disabled={mode === "detail"}>
                <legend>Offer and billing</legend>
                <label>Offer type <select aria-label="Offer type" value={product.offerType || ""} onChange={e => { this.updateEditingProduct(product.id, "offerType", e.target.value || null); if (!e.target.value || e.target.value === "call") this.updateEditingProduct(product.id, "billingInterval", null); }}>
                  <option value="">Shop product</option><option value="membership">App membership</option><option value="group">Group coaching</option><option value="call">Paid call</option><option value="program">One-to-one program</option>
                </select></label>
                {product.offerType && <>
                  <p><label>Billing <select aria-label="Billing" value={product.billingInterval || ""} disabled={product.offerType === "call"} onChange={e => this.updateEditingProduct(product.id, "billingInterval", e.target.value || null)}>
                    <option value="">One-time payment</option><option value="week">Every week</option><option value="month">Every month</option><option value="year">Every year</option>
                  </select></label></p>
                  <p><label>Currency <input aria-label="Currency" maxLength={3} value={product.currency || this.props.item?.currency || "USD"} onChange={e => this.updateEditingProduct(product.id, "currency", e.target.value.toUpperCase())} /></label></p>
                  <p><label>Button text <input aria-label="Button text" maxLength={80} value={product.ctaLabel || ""} onChange={e => this.updateEditingProduct(product.id, "ctaLabel", e.target.value)} /></label></p>
                  <p><label><input type="checkbox" checked={product.showAfterOnboarding === true} onChange={e => this.updateEditingProduct(product.id, "showAfterOnboarding", e.target.checked)} /> Use this step for the onboarding CTA</label></p>
                  <p><label>Funnel step <input aria-label="Funnel step" type="number" min={0} max={1000} placeholder="Outside the funnel" value={product.funnelStep ?? ""} onChange={e => this.updateEditingProduct(product.id, "funnelStep", e.target.value === "" ? null : Number(e.target.value))} /></label></p>
                  <p><label>Required access before purchase <input aria-label="Required access before purchase" placeholder="pro" defaultValue={(product.purchaseRequiresEntitlementIds || []).join(", ")} onBlur={e => this.updateEditingProduct(product.id, "purchaseRequiresEntitlementIds", e.target.value.split(",").map(id => id.trim()).filter(Boolean))} /></label></p>
                  <p><label>Hide when member has access <input aria-label="Hide when member has access" placeholder="offer.personal-program" defaultValue={(product.hideWhenEntitlementIds || []).join(", ")} onBlur={e => this.updateEditingProduct(product.id, "hideWhenEntitlementIds", e.target.value.split(",").map(id => id.trim()).filter(Boolean))} /></label></p>
                  <p style={{ fontSize: 12 }}>Steps progress from lowest to highest. Only the next unfinished step is offered. Enter entitlement IDs separated by commas. Hide the consultation when the member already has the personal program.</p>
                  {product.offerType !== "call" && <p><label>Member access link <input aria-label="Member access link" type="url" placeholder="https://… (blank keeps the existing link)" value={product.deliveryURL || ""} onChange={e => this.updateEditingProduct(product.id, "deliveryURL", e.target.value)} /></label></p>}
                  <p style={{ fontSize: 12 }}>{product.offerType === "call" ? "Set the service price, duration and availability in Booking. Use its booking page as the product link below; payment is collected after a time is selected." : "Each offer gets its own access rights. Select those rights on protected events and programs. Put the private group or program link above. Recurring offers must leave the external product link empty. Prices use the shop currency; hidden offers are drafts."}</p>
                </>}
              </fieldset>

              <div style={{ marginBottom: 8 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                  }}
                >
                  Product ID
                </label>
                <input
                  type="text"
                  value={product.id || ""}
                  disabled
                  style={{ width: "100%", padding: 8, backgroundColor: "#f0f0f0" }}
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                  }}
                >
                  Name
                </label>
                <input
                  type="text"
                  value={product.name || ""}
                  disabled={mode === "detail"}
                  onChange={(e) =>
                    this.updateEditingProduct(product.id, "name", e.target.value)
                  }
                  style={{ width: "100%", padding: 8 }}
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                  }}
                >
                  Description
                </label>
                <textarea
                  value={product.description || ""}
                  disabled={mode === "detail"}
                  onChange={(e) =>
                    this.updateEditingProduct(product.id, "description", e.target.value)
                  }
                  style={{ width: "100%", padding: 8, minHeight: 60 }}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "#666",
                      marginBottom: 4,
                    }}
                  >
                    Price
                  </label>
                  <input
                    type="number"
                    value={product.price || 0}
                    disabled={mode === "detail"}
                    onChange={(e) =>
                      this.updateEditingProduct(
                        product.id,
                        "price",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    style={{ width: "100%", padding: 8 }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "#666",
                      marginBottom: 4,
                    }}
                  >
                    Category
                  </label>
                  <input
                    type="text"
                    value={product.category || ""}
                    disabled={mode === "detail"}
                    onChange={(e) =>
                      this.updateEditingProduct(product.id, "category", e.target.value)
                    }
                    style={{ width: "100%", padding: 8 }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 8 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                  }}
                >
                  Product Image
                </label>
                <ImageUpload
                  field={this.createProductFieldForImageUpload(product.id)}
                  form={this.createProductFormForImageUpload(product.id)}
                  actionId={product.id}
                  reference={"shop/products"}
                />
              </div>

              <div style={{ marginBottom: 8 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                  }}
                >
                  Item Link
                </label>
                <input
                  type="text"
                  placeholder="External link for this product"
                  value={product.itemLink || ""}
                  disabled={mode === "detail"}
                  onChange={(e) =>
                    this.updateEditingProduct(product.id, "itemLink", e.target.value)
                  }
                  style={{ width: "100%", padding: 8 }}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "#666",
                      marginBottom: 4,
                    }}
                  >
                    Index
                  </label>
                  <input
                    type="number"
                    value={product.index ?? 0}
                    disabled={mode === "detail"}
                    onChange={(e) =>
                      this.updateEditingProduct(
                        product.id,
                        "index",
                        parseInt(e.target.value) || 0
                      )
                    }
                    style={{ width: "100%", padding: 8 }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "#666",
                      marginBottom: 4,
                    }}
                  >
                    Visible
                  </label>
                  <select
                    value={product.isVisible ? "true" : "false"}
                    disabled={mode === "detail"}
                    onChange={(e) =>
                      this.updateEditingProduct(
                        product.id,
                        "isVisible",
                        e.target.value === "true"
                      )
                    }
                    style={{ width: "100%", padding: 8 }}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              {mode !== "detail" && this.props.communityId && <PrivatePdfUpload
                communityId={this.props.communityId} productId={product.id}
                saved={products.some(p => p.id === product.id)} />}
              {mode !== "detail" && (
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => this.saveProduct(product)}
                  >
                    Save Product
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => this.removeProduct(product.id)}
                  >
                    Delete Product
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => this.cancelEditingProduct(product.id)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          ))}

          {mode !== "detail" && (
            <Button variant="contained" color="primary" onClick={this.addProduct}>
              Add Product
            </Button>
          )}
        </div>
      </Section>
    );
  };

  render() {
    const { onChageFormToEditMode, onRemoveItem, isSubmitting, mode, isActive } = this.props;

    return (
      <Wrapper>
        <Form>
          {this.renderBasicInfoSection()}
          {this.renderPaymentSection()}
          {this.renderProductsSection()}
          <ActionFormButton
            onChageFormToEditMode={onChageFormToEditMode}
            onClick={() => {
              console.log("Handling Submit");
              this.props.handleSubmit();
            }}
            onRemoveItem={onRemoveItem}
            disabled={isSubmitting}
            entity="Shop"
            canBeRemoved
            mode={mode}
            hidden={!isActive}
          />
        </Form>
      </Wrapper>
    );
  }
}

const validationSchema = Yup.object().shape({
  shopName: Yup.string().required("Shop name is required"),
  currency: Yup.string().required("Currency is required"),
  isActive: Yup.mixed().oneOf(["true", "false"]),
  paymentLink: Yup.string().url("Must be a valid URL"),
  paypalEmail: Yup.string().email("Must be a valid email"),
});

const enhance = withFormik<OuterProps, FormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ item }: OuterProps): FormValues => ({
    id: item?.id || "",
    shopName: item?.shopName || "",
    currency: item?.currency || "USD",
    isActive: item?.isActive !== undefined ? String(item.isActive) : "true",
    paymentLink: item?.paymentLink || "",
    paypalEmail: item?.paypalEmail || "",
  }),
  validationSchema,
  handleSubmit(values, { setSubmitting, props }) {
    console.log("Submitting Shop form with values:", values);

    const { onCreateItem, onEditItem, mode } = props;
    const properCallback = mode === "edit" ? onEditItem : onCreateItem;

    const shop: Shop = {
      id: values.id,
      shopName: values.shopName,
      currency: values.currency,
      isActive: values.isActive === "true",
      paymentLink: values.paymentLink,
      paypalEmail: values.paypalEmail,
    };

    properCallback(shop);
    setSubmitting(false);
  },
  //@ts-ignore
})(ShopForm);

// Functional wrapper to use the hook
const ShopFormWithProducts = (props: OuterProps) => {
  const products = useAllProducts({ communityId: props.communityId });

  return <EnhancedShopForm {...props} loadedProducts={products} />;
};

const EnhancedShopForm = enhance;

export default ShopFormWithProducts;
